import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from "src/components/ui/dropdown-menu";
import X from "src/assets/icons-redesign/x.svg?react";
import { ReactElement, useEffect, useMemo, useState } from "react";
import { Badge } from "src/components/ui/badge";
import { AnswerSource, QuestionStats, SurveyAudienceCriteria, SurveyStatus } from "src/redux/survey/survey.types";
import AddFilterButton from "./add-filter-button.component";
import { useAttributeList } from "src/hooks/useAttributeList";
import { toCityCase } from "src/helpers/string.helpers";
import DemographicsTable from "./demographics-table";
import {Attribute} from "../../../components/criteria/criteria-settings";

interface FilterTagProps {
  readonly availableDmas?: Attribute;
  readonly surveyTypeAttributes?: Attribute[];
  readonly initialFilter?: SurveyAudienceCriteria;
  readonly filter: keyof SurveyAudienceCriteria;
  readonly selected: string[];
  readonly updateFilter: (key: keyof SurveyAudienceCriteria, selected: string[]) => void;
}

const FilterTag = ({ availableDmas, surveyTypeAttributes, initialFilter, filter, selected, updateFilter }: FilterTagProps): ReactElement | null => {
  const { isLoading, attributes } = useAttributeList(initialFilter, availableDmas);
  const attributeWithOptions = useMemo(() => {
    if (!filter) return undefined;
    const fromSurveyType = surveyTypeAttributes?.find((a) => a.value === filter);
    if (fromSurveyType?.options?.length) return fromSurveyType;
    if (!attributes || isLoading) return undefined;
    return attributes.find((local) => local.value === filter);
  }, [filter, attributes, isLoading, surveyTypeAttributes]);

  const options = attributeWithOptions?.options;

  if (!selected || !options) {
    return null;
  }

  const filterString = filter === 'DMA' ? 'Location' : filter;
  const selectedString = selected
    .map((s) => (filter === 'DMA' ? toCityCase(s) : options.find((o) => o.value === s)?.label ?? toCityCase(s)))
    .join(', ');

  const handleRemove = () => {
    updateFilter(filter, []);
  };

  const handleCheck = (att: string) => {
    if (selected.some(val => val === att)) {
      updateFilter(filter, selected.filter(val => val !== att));
      return;
    }

    updateFilter(filter, [...selected, att]);
  };

  return (
    <div className="flex shrink">
      <Badge variant="demo-filter">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div className="flex gap-2">
              <div className="py-2 text-[#50555C] whitespace-nowrap">
                {filterString}
              </div>
              <div className="py-2 max-w-40 truncate">
                {selectedString}
              </div>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {options.map(attribute => {
              return (
                <DropdownMenuCheckboxItem
                  className="text-base"
                  checked={selected.some(item => item === attribute.value)}
                  onCheckedChange={() => handleCheck(attribute.value)}
                  key={attribute.value}
                  onSelect={(e) => e.preventDefault()}
                >
                  <span>
                    {attribute.label}
                  </span>
                </DropdownMenuCheckboxItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="border-l-1 border-gray-light-10 h-full w-0" />
        <div onClick={handleRemove}>
          <X />
        </div>
      </Badge>
    </div >
  );
};

interface ResultsQuestionFilterProps {
  readonly stats: QuestionStats;
  readonly sourceFilter: AnswerSource;
  readonly initialFilter?: SurveyAudienceCriteria;
  readonly surveyStatus?: SurveyStatus;
}

const ResultsQuestionFilter = ({ stats, sourceFilter, initialFilter, surveyStatus }: ResultsQuestionFilterProps) => {
  const [filter, setFilter] = useState(initialFilter);

  const availableDmas = useMemo(() => {
    if (!stats) {
      return undefined;
    }

    const dmas = stats.answers
      .reduce((acc, question) => {
        question.demographics?.dma?.forEach((dma) => {
          if (!acc.includes(dma.title)) {
            acc.push(dma.title);
          }
        });

        return acc;
      }, [] as string[])
      .sort((a, b) => a.localeCompare(b))
      .map((dma) => ({ label: toCityCase(dma), value: dma }));

    return {
      label: 'Location',
      value: 'DMA',
      options: dmas,
    };
  }, [stats]);

  const updateFilter = (key: keyof SurveyAudienceCriteria, selected: string[]) => {
    if (!filter) {
      return;
    }

    setFilter({ ...filter, [key]: selected.length ? selected : undefined });
  };

  // this ensures that the filter drilling gets applied to all children
  useEffect(() => {
    setFilter(initialFilter);
  }, [initialFilter]);

  const selectedCriteria = useMemo(() => {
    if (!filter) {
      return [];
    }

    return Object.entries(filter).map(([key, value]) => {
      return {
        title: key as keyof SurveyAudienceCriteria,
        values: value,
      };
    });
  }, [initialFilter, filter]);

  const renderTables = () => {
    if (!selectedCriteria.length) {
      return null;
    }

    const validCriteria = selectedCriteria.filter(crit => crit.values?.length > 0);

    if (!validCriteria.length) {
      return null;
    }

    return (
      <div className="flex flex-col gap-6">
        {validCriteria.map(crit => (
          <DemographicsTable
            filter={crit}
            key={crit.title}
            sourceFilter={sourceFilter}
            question={stats}
            surveyStatus={surveyStatus}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-row gap-2 flex-wrap">
        {selectedCriteria.map(audience => (
          <FilterTag
            availableDmas={availableDmas}
            filter={audience.title}
            selected={audience.values}
            updateFilter={updateFilter}
            key={audience.title}
          />
        ))}
        <AddFilterButton
          availableDmas={availableDmas}
          filter={filter}
          setFilter={setFilter}
        />
      </div>
      {renderTables()}
    </div>
  );
};

export { FilterTag };
export default ResultsQuestionFilter;
